
import { defineComponent } from "vue";
import store from "@/store";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import { FamilyPlanManagerViewModel } from "@/interfaces/FamilyPlanManagerViewModel";
import swal from "sweetalert";
import { FamilyUserStatusIDEnum } from "@/interfaces/FamilyUserStatusIDEnum";

export default defineComponent({
  name: "FamilyPlanManager",
  components: {
    DataTable,
    Column,
    Button,
    InputText,
  },
  data() {
    return {
      familyPlanMembers: [] as FamilyPlanManagerViewModel[],
      showAddMemberForm: false,
      newMemberEmail: "",
    };
  },

  computed: {
    visibleFamilyMembers(): FamilyPlanManagerViewModel[] {
      return this.familyPlanMembers.filter((member) => member.familyUserStatusID !== FamilyUserStatusIDEnum.Removed && member.familyUserStatusID !== FamilyUserStatusIDEnum.RequestCancelled);
    },
  },
  async mounted() {
    await this.fetchDetails();
  },
  methods: {
    async fetchDetails() {
      try {
        const response = await this.$store.dispatch("GetFamilyPlanDetails");
        if (!response || !response.success) {
          swal("Error", "Error loading family details. Please try again.");
          return;
        }
        if (response && response.data) {
          this.familyPlanMembers = response.data;
        }
      } catch (error) {
        swal("Error", "Error loading family details. Please try again.");
      }
    },
    isValidEmail(email: string): boolean {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    isEmailAlreadyAdded(email: string): boolean {
      return this.visibleFamilyMembers.some((member) => member.associatedUserEmail?.toLowerCase() === email.toLowerCase());
    },

    async confirmAction(message: string): Promise<boolean> {
      return swal({
        title: "Are you sure?",
        text: message,
        icon: "warning",
        buttons: ["Cancel", "Yes"],
        dangerMode: true,
      });
    },
    getStatusText(statusID: number): string {
      const statusMap: { [key: number]: string } = {
        [FamilyUserStatusIDEnum.Requested]: "Requested",
        [FamilyUserStatusIDEnum.Accepted]: "Accepted",
        [FamilyUserStatusIDEnum.Denied]: "Denied",
      };
      return statusMap[statusID] || "Unknown";
    },

    getActionLabel(statusID: FamilyUserStatusIDEnum): string {
      switch (statusID) {
        case FamilyUserStatusIDEnum.Denied:
          return "Request Again";
        case FamilyUserStatusIDEnum.Requested:
          return "Cancel";
        case FamilyUserStatusIDEnum.Accepted:
          return "Remove";
        default:
          return "N/A";
      }
    },

    getActionClass(statusID: FamilyUserStatusIDEnum): string {
      switch (statusID) {
        case FamilyUserStatusIDEnum.Denied:
          return "p-button-success";
        case FamilyUserStatusIDEnum.Requested:
          return "p-button-warning";
        case FamilyUserStatusIDEnum.Accepted:
          return "p-button-danger";
        default:
          return "p-button-secondary";
      }
    },

    handleAction(member: FamilyPlanManagerViewModel) {
      switch (member.familyUserStatusID) {
        case FamilyUserStatusIDEnum.Denied:
          this.requestAddMember(member);
          break;
        case FamilyUserStatusIDEnum.Requested:
          this.cancelAddMemberRequest(member);
          break;
        case FamilyUserStatusIDEnum.Accepted:
          this.removeMember(member);
          break;
        default:
          swal("Action not available", "This member has an unrecognized status.", "info");
      }
    },

    async removeMember(member: FamilyPlanManagerViewModel) {
      try {
        const confirmed = await this.confirmAction(`Are you sure that you want to remove ${member.associatedUserEmail} from your plan? This user will lose access to this subscription and be downgraded to a free plan if available.`);
        if (!confirmed) return;

        // Update the status before dispatching
        const updatedMember: FamilyPlanManagerViewModel = {
          ...member,
          familyUserStatusID: FamilyUserStatusIDEnum.Removed,
        };

        const response = await store.dispatch("UpdateFamilyPlanMember", updatedMember);

        if (response) {
          // Update local state
          const target = this.familyPlanMembers.find((m) => m.associatedUserEmail === member.associatedUserEmail);
          if (target) {
            target.familyUserStatusID = FamilyUserStatusIDEnum.Removed;
          }

          swal("Success", `Successfully removed ${member.associatedUserEmail}.`, "success");
        } else {
          swal("Error", "There was an error removing this family member.", "error");
        }
      } catch (error) {
        swal("Error", "There was an error removing this family member.", "error");
      }
    },

    async cancelAddMemberRequest(member: FamilyPlanManagerViewModel) {
      try {
        const confirmed = await this.confirmAction(`Are you sure that you want to cancel your request to ${member.associatedUserEmail}?`);
        if (!confirmed) return;

        // Update the status before dispatching
        const updatedMember: FamilyPlanManagerViewModel = {
          ...member,
          familyUserStatusID: FamilyUserStatusIDEnum.Removed,
        };

        const response = await store.dispatch("UpdateFamilyPlanMember", updatedMember);

        if (response) {
          // Update local state
          const target = this.familyPlanMembers.find((m) => m.associatedUserEmail === member.associatedUserEmail);
          if (target) {
            target.familyUserStatusID = FamilyUserStatusIDEnum.RequestCancelled;
          }

          swal("Success", `Successfully cancelled request for ${member.associatedUserEmail}.`, "success");
        } else {
          swal("Error", "There was an error cancelling this request.", "error");
        }
      } catch (error) {
        swal("Error", "There was an error cancelling this request.", "error");
      }
    },

    // Method for adding first time users
    async requestAddNewMember() {
      try {
        if (!this.newMemberEmail) return;

        if (this.isEmailAlreadyAdded(this.newMemberEmail)) {
          swal("Notice", `${this.newMemberEmail} is already part of your family plan.`, "info");
          return;
        }

        const confirmed = await this.confirmAction(`Are you sure that you want to send a request to ${this.newMemberEmail}?`);
        if (!confirmed) return;

        let newMember: FamilyPlanManagerViewModel;

        newMember = {
          iD: null,
          affiliateID: null,
          mainUserID: 0,
          associatedUserID: null,
          associatedUserEmail: this.newMemberEmail,
          familyUserStatusID: FamilyUserStatusIDEnum.Requested,
          createdDate: null,
          modifiedDate: null,
          active: true,
          associatedUserFirstName: null,
          associatedUserLastName: null,
        };

        const response = await store.dispatch("UpdateFamilyPlanMember", newMember);

        if (response.success) {
          const existingMember = this.familyPlanMembers.find((m) => m.associatedUserEmail?.toLowerCase() === this.newMemberEmail.toLowerCase());

          if (existingMember) {
            // Update existing member's status instead of adding a new one
            existingMember.familyUserStatusID = FamilyUserStatusIDEnum.Requested;
          } else {
            this.familyPlanMembers.push(newMember);
          }
          swal("Success", `A request has been sent to ${newMember.associatedUserEmail}.`, "success");
        } else {
          // Check for response.error as a non-empty array. If it has length then use that error instead of the default.
          if (response && Array.isArray(response.errors) && response.errors.length > 0) {
            const message = response.errors[0];
            swal("Error", message, "error");
          } else {
            swal("Error", "There was an error completing this request. Please refresh and try again.", "error");
          }
        }

        this.newMemberEmail = "";
        this.showAddMemberForm = false;
      } catch (error) {
        swal("Error", "There was an error completing this request. Please refresh and try again.", "error");
      }
    },

    // Method for adding existing users
    async requestAddMember(member: FamilyPlanManagerViewModel) {
      const confirmed = await this.confirmAction(`Are you sure that you want to send another request to ${member.associatedUserEmail}?`);
      if (!confirmed) return;

      try {
        if (!member) return;

        const updatedMember: FamilyPlanManagerViewModel = {
          ...member,
          familyUserStatusID: FamilyUserStatusIDEnum.Requested,
        };

        const response = await store.dispatch("UpdateFamilyPlanMember", updatedMember);

        if (response) {
          swal("Success", `A request has been sent to ${updatedMember.associatedUserEmail}.`, "success");

          // Update local state
          const target = this.familyPlanMembers.find((m) => m.associatedUserEmail === member.associatedUserEmail);
          if (target) {
            target.familyUserStatusID = FamilyUserStatusIDEnum.Requested;
          }
        } else {
          swal("Error", "There was an error completing this request. Please refresh and try again.", "error");
        }

        this.newMemberEmail = "";
        this.showAddMemberForm = false;
      } catch (error) {
        swal("Error", "There was an error completing this request. Please refresh and try again.", "error");
      }
    },

    cancelAddMember() {
      this.newMemberEmail = "";
      this.showAddMemberForm = false;
    },
  },
});
